import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import LoaderBox from "../HomePage/LoaderBox";

const AdminRaspored = ({ turnir }) => {
  const [dani, setDani] = useState(["Utakmice"]);
  const [activeDan, setActiveDan] = useState("Utakmice");
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScheduleVisible, setIsScheduleVisible] = useState(false);
  const tournamentId = turnir?.id;

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  // Automatsko generiranje dana na temelju datum_od i datum_do iz tablice turnira
  useEffect(() => {
    if (turnir && turnir.datum_od && turnir.datum_do) {
      const startDate = new Date(turnir.datum_od);
      const endDate = new Date(turnir.datum_do);
      const generiraniDani = ["Utakmice"];

      const daniTjedna = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];

      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dayName = daniTjedna[currentDate.getDay()];
        const dayNum = String(currentDate.getDate()).padStart(2, "0");
        const monthNum = String(currentDate.getMonth() + 1).padStart(2, "0");

        generiraniDani.push(`${dayName} ${dayNum}.${monthNum}.`);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      setDani(generiraniDani);
    }
  }, [turnir]);

  // Funkcija za direktnu promjenu i spremanje u bazu
  const handleToggleSchedule = async (newValue) => {
    setIsScheduleVisible(newValue);
    if (!tournamentId) return;

    await supabase
      .from("admin_turniri")
      .update({ prikazi_raspored: newValue })
      .eq("id", tournamentId);
  };

  // Generiranje satnica svakih 5 minuta (optimizirano s useMemo)
  const satnice = useMemo(() => {
    const times = [];
    for (let h = 9; h <= 23; h++) {
      for (let m = 0; m < 60; m += 5) {
        const minutesFormatted = String(m).padStart(2, "0");
        times.push(`${h}:${minutesFormatted}`);
      }
    }
    times.push("00:00");
    return times;
  }, []);

  const loadData = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);

    const { data: settingData } = await supabase
      .from("admin_turniri")
      .select("prikazi_raspored")
      .eq("id", tournamentId)
      .single();

    if (settingData) {
      setIsScheduleVisible(settingData.prikazi_raspored);
    }

    // 1. Dohvati regularne timove i veteranske timove
    const { data: teamsData } = await supabase
      .from("turniri_timovi")
      .select("*")
      .eq("turnir_id", tournamentId);

    const { data: veteranTeamsData } = await supabase
      .from("turniri_veterani_timovi")
      .select("*")
      .eq("turnir_id", tournamentId);

    // Označi veteranske timove da ih lakše prepoznamo
    const markedVeteranTeams = (veteranTeamsData || []).map((t) => ({
      ...t,
      isVeteran: true,
    }));
    setTeams([...(teamsData || []), ...markedVeteranTeams]);

    // 2. Dohvati regularne utakmice i veteranske utakmice
    const { data: existingMatches } = await supabase
      .from("turniri_utakmice")
      .select("*")
      .eq("tournament_id", tournamentId);

    const { data: existingVeteranMatches } = await supabase
      .from("turniri_veterani")
      .select("*")
      .eq("tournament_id", tournamentId);

    const markedVeteranMatches = (existingVeteranMatches || []).map((m) => ({
      ...m,
      isVeteran: true,
    }));

    let finalMatchesList = [];
    if (existingMatches) {
      // Filtriramo duplikate iz završnice (ako postoji više zapisa za isti bracket_slot_id, uzimamo samo onaj gdje su popunjeni timovi ili prvi valjani)
      const uniqueMap = new Map();
      existingMatches.forEach((m) => {
        if (m.bracket_slot_id) {
          const key = `slot-${m.bracket_slot_id}`;
          // Ako već imamo zapis za ovaj slot, preferiraj onaj koji ima barem jednog tima ili je dovršen
          if (uniqueMap.has(key)) {
            const existing = uniqueMap.get(key);
            if (
              (!existing.team_a_id && m.team_a_id) ||
              (!existing.team_b_id && m.team_b_id)
            ) {
              uniqueMap.set(key, m);
            }
          } else {
            uniqueMap.set(key, m);
          }
        } else {
          // Regularne utakmice po grupama
          uniqueMap.set(`reg-${m.team_a_id}-${m.team_b_id}`, m);
        }
      });
      finalMatchesList = Array.from(uniqueMap.values());
    }

    if (markedVeteranMatches) {
      finalMatchesList = [...finalMatchesList, ...markedVeteranMatches];
    }

    // 3. Automatsko generiranje parova za regularne grupe
    const groups = ["A", "B", "C", "D", "E", "F", "G", "H"];
    groups.forEach((gr) => {
      const teamsInGroup = (teamsData || []).filter((t) => t.group_name === gr);
      for (let i = 0; i < teamsInGroup.length; i++) {
        for (let j = i + 1; j < teamsInGroup.length; j++) {
          const tA = teamsInGroup[i];
          const tB = teamsInGroup[j];

          const exists = finalMatchesList.find(
            (m) =>
              !m.isVeteran &&
              ((m.team_a_id === tA.id && m.team_b_id === tB.id) ||
                (m.team_a_id === tB.id && m.team_b_id === tA.id)),
          );

          if (!exists) {
            finalMatchesList.push({
              tournament_id: tournamentId,
              group_name: gr,
              team_a_id: tA.id,
              team_b_id: tB.id,
              match_day: "",
              match_time: "",
            });
          }
        }
      }
    });

    // 4. Automatsko generiranje parova za veteranske grupe
    groups.forEach((gr) => {
      const teamsInGroup = markedVeteranTeams.filter(
        (t) => t.group_name === gr,
      );
      for (let i = 0; i < teamsInGroup.length; i++) {
        for (let j = i + 1; j < teamsInGroup.length; j++) {
          const tA = teamsInGroup[i];
          const tB = teamsInGroup[j];

          const exists = finalMatchesList.find(
            (m) =>
              m.isVeteran &&
              ((m.team_a_id === tA.id && m.team_b_id === tB.id) ||
                (m.team_a_id === tB.id && m.team_b_id === tA.id)),
          );

          if (!exists) {
            finalMatchesList.push({
              tournament_id: tournamentId,
              group_name: gr,
              team_a_id: tA.id,
              team_b_id: tB.id,
              match_day: "",
              match_time: "",
              isVeteran: true,
            });
          }
        }
      }
    });

    setMatches(finalMatchesList);
    setLoading(false);
  }, [tournamentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateMatchInDb = async (match, field, value) => {
    const dbField = field === "dan" ? "match_day" : "match_time";
    const targetTable = match.isVeteran
      ? "turniri_veterani"
      : "turniri_utakmice";

    const newDay = field === "dan" ? value : match.match_day;
    const newTime = field === "vrijeme" ? value : match.match_time;

    setMatches((prev) =>
      prev.map((m) => {
        const isSameMatch = m.id
          ? m.id === match.id
          : m.team_a_id === match.team_a_id &&
            m.team_b_id === match.team_b_id &&
            m.isVeteran === match.isVeteran;

        return isSameMatch ? { ...m, [dbField]: value } : m;
      }),
    );

    if (match.id) {
      await supabase
        .from(targetTable)
        .update({ [dbField]: value })
        .eq("id", match.id);
    } else {
      const { data, error } = await supabase
        .from(targetTable)
        .insert([
          {
            tournament_id: tournamentId,
            team_a_id: match.team_a_id,
            team_b_id: match.team_b_id,
            group_name: match.group_name,
            match_day: newDay,
            match_time: newTime,
            is_completed: false,
            scorea: 0,
            scoreb: 0,
          },
        ])
        .select();

      if (error) {
        console.error("Greška pri spremanju utakmice:", error.message);
        alert("Greška pri spremanju u bazu: " + error.message);
      } else if (data && data.length > 0) {
        const savedMatch = { ...data[0], isVeteran: match.isVeteran };
        setMatches((prev) =>
          prev.map((m) =>
            m.team_a_id === match.team_a_id &&
            m.team_b_id === match.team_b_id &&
            m.isVeteran === match.isVeteran
              ? savedMatch
              : m,
          ),
        );
      }
    }
  };

  // Optimizirano filtriranje pomoću useMemo
  const displayedMatches = useMemo(() => {
    if (activeDan === "Utakmice") {
      return matches.filter(
        (m) => !m.is_completed && (!m.match_day || !m.match_time),
      );
    } else {
      return matches
        .filter((m) => m.match_day === activeDan && m.match_time)
        .sort((a, b) => a.match_time.localeCompare(b.match_time));
    }
  }, [matches, activeDan]);

  if (loading) return <LoaderBox text="Učitavanje rasporeda..." />;

  return (
    <div className="admin-raspored">
      <div className="visibility-control-bar">
        <span className="control-label">
          Prikaži raspored na webu
          {isLocalhost && <small className="localhost-badge"> </small>}
        </span>

        <label className="ios-switch">
          <input
            type="checkbox"
            id="prikaziRasporedSwitch"
            name="prikazi_raspored"
            checked={isScheduleVisible}
            onChange={(e) => handleToggleSchedule(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
      </div>
      <div className="day-navigation">
        {dani.map((d) => (
          <button
            key={d}
            className={`${activeDan === d ? "active" : ""} ${d === "Utakmice" ? "unassigned-tab" : ""}`}
            onClick={() => setActiveDan(d)}
          >
            {d}
          </button>
        ))}
      </div>

      <div className="raspored-content">
        <section className="assigned-section">
          <h4>
            {activeDan === "Utakmice"
              ? "Utakmice na čekanju"
              : `Raspored za dan: ${activeDan}`}
          </h4>
          <div className="matches-list">
            {displayedMatches.length > 0 ? (
              displayedMatches.map((m) => (
                <MatchRow
                  key={m.id || `temp-${m.team_a_id}-${m.team_b_id}`}
                  match={m}
                  satnice={satnice}
                  dani={dani.filter((d) => d !== "Utakmice")}
                  update={updateMatchInDb}
                  teams={teams}
                  allMatches={matches}
                />
              ))
            ) : (
              <p className="empty-info">Nema utakmica u ovoj kategoriji.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

// OPTIMIZIRANA POMOĆNA KOMPONENTA ZA REDAK UTAKMICE (React.memo + useMemo za zauzeta vremena)
const MatchRow = React.memo(
  ({ match, satnice, dani, update, teams, allMatches }) => {
    const tA = teams.find((t) => t.id === match.team_a_id)?.name || "???";
    const tB = teams.find((t) => t.id === match.team_b_id)?.name || "???";

    const getFazaLabel = () => {
      if (!match.bracket_slot_id) return match.group_name;

      const id = match.bracket_slot_id;
      if (id >= 1 && id <= 8) return "1/8";
      if (id >= 9 && id <= 12) return "1/4";
      if (id >= 13 && id <= 14) return "1/2";
      if (id === 15) return "Fin";
      if (id === 16) return "3.mj";
      return "Z";
    };

    const takenTimesSet = useMemo(() => {
      const set = new Set();
      if (!match.match_day) return set;

      allMatches.forEach((m) => {
        if (m.match_day === match.match_day && m.match_time) {
          const isSelf = m.id
            ? m.id === match.id
            : m.team_a_id === match.team_a_id &&
              m.team_b_id === match.team_b_id;

          if (!isSelf) {
            set.add(m.match_time);
          }
        }
      });
      return set;
    }, [
      allMatches,
      match.match_day,
      match.id,
      match.team_a_id,
      match.team_b_id,
    ]);

    const isTimeTaken = (vrijeme) => takenTimesSet.has(vrijeme);

    return (
      <div
        className={`admin-match-row ${match.is_completed ? "completed-match-row" : ""}`}
      >
        {/* Oznaka za veterane u gornjem lijevom kutu */}
        {match.isVeteran && (
          <span className="veteran-corner-badge">VETERANI</span>
        )}

        <div
          className={`group-tag ${match.bracket_slot_id ? "knockout-tag" : ""}`}
        >
          {getFazaLabel()}
        </div>

        <div className="teams-info">
          <div className="team-line">
            <span className="team-name">{tA}</span>
            {match.is_completed && (
              <span className="match-score">{match.scorea}</span>
            )}
          </div>
          <div className="team-line">
            <span className="team-name">{tB}</span>
            {match.is_completed && (
              <span className="match-score">{match.scoreb}</span>
            )}
          </div>
        </div>

        <div className="selectors">
          {match.is_completed ? (
            <div className="completed-info" style={{ textAlign: "center" }}>
              <div className="completed-badge">ODIGRANO</div>
              <span
                className="played-time-label"
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginTop: "4px",
                  color: "#9b9999ff",
                  fontWeight: "600",
                }}
              >
                {match.match_time}
              </span>
            </div>
          ) : (
            <>
              <select
                name={`match-day-${match.id || match.team_a_id + "-" + match.team_b_id}`}
                value={match.match_day || ""}
                onChange={(e) => update(match, "dan", e.target.value)}
              >
                <option value="">Odaberi dan</option>
                {dani.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>

              <select
                name={`match-time-${match.id || match.team_a_id + "-" + match.team_b_id}`}
                value={match.match_time || ""}
                onChange={(e) => update(match, "vrijeme", e.target.value)}
                disabled={!match.match_day}
              >
                <option value="">Odaberi vrijeme</option>
                {satnice.map((s) => (
                  <option key={s} value={s} disabled={isTimeTaken(s)}>
                    {s}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>
    );
  },
);

export default AdminRaspored;
